import {
	EventSubscriberType,
	GlobalEventSubscriberType,
} from 'src/environmentVariables/EnvironmentVariableTypes';
import { create } from 'zustand';
import { produce, Draft } from 'immer';
import { dlog } from '../dev';
import EVENTS_SUBSCRIPTION_UTILS from '../utils/EventsSubscription/eventsSubscriptionUtils';

// # See README.md in src/hooks/driftEvents for more information on the subscription state

export type EventsSubscriptionState<T> = {
	subscriptionType: T;
};

export interface EventsSubscriptionStateStore {
	set: (fn: (state: Draft<EventsSubscriptionStateStore>) => void) => void;
	get: () => EventsSubscriptionStateStore;

	// Subscription states
	selfEventsState: EventsSubscriptionState<EventSubscriberType>;
	globalEventsState: EventsSubscriptionState<GlobalEventSubscriberType>;

	// Setters
	updateSelfEventSubscriberType: (type: EventSubscriberType) => void;
	updateGlobalEventSubscriberType: (type: GlobalEventSubscriberType) => void;
	handleGlobalEventsFallback: () => void;
	handleGlobalEventsFallbackUp: () => void;
}

const GLOBAL_EVENTS_FALLBACK_STATE = {
	lastFallbackTime: 0,
};

const GLOBAL_EVENTS_FALLUP_COOLDOWN_STATE = {
	count: 0,
	lastFallupTime: 0,
};

const FALL_UP_GRACE_PERIOD_MS = 1000 * 60;

const getCurrentCooldownTime = () => {
	return 1000 * 60 * 2 ** GLOBAL_EVENTS_FALLUP_COOLDOWN_STATE.count;
};

const checkFallupCooldownState = () => {
	// If the most recent fallback was too recent, don't fall up
	if (
		GLOBAL_EVENTS_FALLBACK_STATE.lastFallbackTime >
		Date.now() - FALL_UP_GRACE_PERIOD_MS
	) {
		return false;
	}

	// Use FALLUP_COUNT to determine an exponential backoff on falling up in case we keep failing repeatedly
	if (GLOBAL_EVENTS_FALLUP_COOLDOWN_STATE.count > 0) {
		const timeSinceLastFallback =
			Date.now() - GLOBAL_EVENTS_FALLUP_COOLDOWN_STATE.lastFallupTime;

		const cooldownTime = getCurrentCooldownTime();

		if (timeSinceLastFallback < cooldownTime) {
			return false;
		}
	}

	return true;
};

const handleFallupCooldownIncrement = () => {
	GLOBAL_EVENTS_FALLUP_COOLDOWN_STATE.count++;
	GLOBAL_EVENTS_FALLUP_COOLDOWN_STATE.lastFallupTime = Date.now();
};

const handleFallbackIncrement = () => {
	GLOBAL_EVENTS_FALLBACK_STATE.lastFallbackTime = Date.now();
};

const getInitialSelfEventsState =
	(): EventsSubscriptionState<EventSubscriberType> => {
		const preferredEventSubscriberType =
			EVENTS_SUBSCRIPTION_UTILS.getPreferredSelfSubscriptionType({
				ignoreUserSettings: true, // We need to ignore user settings on initialization because it is incompatible with server-side rendering. Instead this value will be initialized again in @useSyncEventsSubscriptionState
			});

		return {
			subscriptionType: preferredEventSubscriberType,
		};
	};

const getInitialGlobalEventsState =
	(): EventsSubscriptionState<GlobalEventSubscriberType> => {
		const preferredGlobalEventSubscriberType =
			EVENTS_SUBSCRIPTION_UTILS.getPreferredGlobalEventSubscriptionType({
				ignoreUserSettings: true, // We need to ignore user settings on initialization because it is incompatible with server-side rendering. Instead this value will be initialized again in @useSyncEventsSubscriptionState
			});

		return {
			subscriptionType: preferredGlobalEventSubscriberType,
		};
	};

const useEventsSubscriptionStateStore = create<EventsSubscriptionStateStore>(
	(set, get): EventsSubscriptionStateStore => {
		const setState = (
			fn: (state: Draft<EventsSubscriptionStateStore>) => void
		) => set(produce(fn));

		return {
			set: setState,
			get: () => get(),

			// Initial states
			selfEventsState: getInitialSelfEventsState(),
			globalEventsState: getInitialGlobalEventsState(),

			// Setters
			updateSelfEventSubscriberType: (type: EventSubscriberType) => {
				setState((state) => {
					state.selfEventsState.subscriptionType = type;
				});
			},
			updateGlobalEventSubscriberType: (type: GlobalEventSubscriberType) => {
				setState((state) => {
					state.globalEventsState.subscriptionType = type;
				});
			},
			handleGlobalEventsFallback: () => {
				handleFallbackIncrement();

				const currentSubscriptionType =
					get().globalEventsState.subscriptionType;

				setState((state) => {
					state.globalEventsState.subscriptionType =
						EVENTS_SUBSCRIPTION_UTILS.getGlobalFallbackOption(
							currentSubscriptionType
						);
				});
			},
			handleGlobalEventsFallbackUp: () => {
				const fallupAllowed = checkFallupCooldownState();

				if (!fallupAllowed) {
					dlog(
						`optimised_event_subscriptions`,
						`skipping_fall_up_attempt_because_cooldown_not_passed (current cooldown time: ${
							getCurrentCooldownTime() / 1000 / 60
						}mins)`
					);
					return;
				}

				const currentSubscriptionType =
					get().globalEventsState.subscriptionType;

				const preferredGlobalEventSubscriptionType =
					EVENTS_SUBSCRIPTION_UTILS.getPreferredGlobalEventSubscriptionType({
						ignoreUserSettings: false,
					});

				const fallUpNotNecessary =
					currentSubscriptionType === preferredGlobalEventSubscriptionType;

				if (fallUpNotNecessary) {
					dlog(
						`optimised_event_subscriptions`,
						`skipping_fall_up_attempt_because_fall_up_not_necessary`
					);
					return;
				}

				handleFallupCooldownIncrement();

				dlog(
					`optimised_event_subscriptions`,
					`falling_up_global_events_subscription_to_${preferredGlobalEventSubscriptionType}`
				);

				setState((state) => {
					state.globalEventsState.subscriptionType =
						preferredGlobalEventSubscriptionType;
				});
			},
		};
	}
);

export default useEventsSubscriptionStateStore;
